import { format, intervalToDuration, parseISO, isValid } from 'date-fns';
import { de } from 'date-fns/locale';

type DateInput = Date | string;

function toDate(input: DateInput): Date | null {
  if (input instanceof Date) {
    return isValid(input) ? input : null;
  }
  const normalized = input.includes('T') ? input : input.replace(' ', 'T');
  try {
    const d = parseISO(normalized);
    return isValid(d) ? d : null;
  } catch {
    return null;
  }
}

/** dd.MM.yyyy  z.B. "22.02.2026" */
export function formatDate(input: DateInput): string {
  const d = toDate(input);
  if (!d) return String(input);
  return format(d, 'dd.MM.yyyy', { locale: de });
}

/** dd.MM.  z.B. "22.02." */
export function formatDateShort(input: DateInput): string {
  const d = toDate(input);
  if (!d) return String(input);
  return format(d, 'dd.MM.', { locale: de });
}

/** d. MMMM yyyy  z.B. "22. Februar 2026" */
export function formatDateLong(input: DateInput): string {
  const d = toDate(input);
  if (!d) return String(input);
  return format(d, 'd. MMMM yyyy', { locale: de });
}

/** d. MMM  z.B. "22. Feb." */
export function formatDateMonthShort(input: DateInput): string {
  const d = toDate(input);
  if (!d) return String(input);
  return format(d, 'd. MMM', { locale: de });
}

/** EEE dd.MM.yyyy  z.B. "Mo 22.02.2026" */
export function formatDateWithWeekday(input: DateInput): string {
  const d = toDate(input);
  if (!d) return String(input);
  return format(d, 'EEE dd.MM.yyyy', { locale: de });
}

/** HH:mm Uhr  z.B. "14:30 Uhr" */
export function formatTime(input: DateInput): string {
  // Reine Uhrzeiten („15:30“, „15:30:00“) sind kein Datum — date-fns liest sie nicht.
  if (typeof input === 'string' && NUR_UHRZEIT.test(input.trim())) {
    return `${toHHMM(input.trim().padStart(5, '0'))} Uhr`;
  }
  const d = toDate(input);
  if (!d) return String(input);
  return format(d, 'HH:mm', { locale: de }) + ' Uhr';
}

const NUR_UHRZEIT = /^\d{1,2}:\d{2}(:\d{2})?$/;

/** HH:mm - HH:mm Uhr  z.B. "14:00 - 15:00 Uhr" */
export function formatTimeRange(start: DateInput, end: DateInput): string {
  const s = toDate(start);
  const e = toDate(end);
  const sf = s ? format(s, 'HH:mm', { locale: de }) : String(start);
  const ef = e ? format(e, 'HH:mm', { locale: de }) : String(end);
  return `${sf} - ${ef} Uhr`;
}

/** HH:mm - HH:mm Uhr aus rohen Zeitstrings (HH:MM oder HH:MM:SS) */
export function formatTimeRangeFromStrings(startTime: string, endTime: string): string {
  return `${toHHMM(startTime)} - ${toHHMM(endTime)} Uhr`;
}

/** dd.MM.yyyy, HH:mm Uhr  z.B. "22.02.2026, 14:30 Uhr" */
export function formatDateTime(input: DateInput): string {
  const d = toDate(input);
  if (!d) return String(input);
  return format(d, 'dd.MM.yyyy', { locale: de }) + ', ' +
         format(d, 'HH:mm', { locale: de }) + ' Uhr';
}

/** yyyy-MM-dd  z.B. "2026-02-22" (fuer API-Aufrufe) */
export function toApiDate(input: DateInput): string {
  const d = toDate(input);
  if (!d) return String(input);
  return format(d, 'yyyy-MM-dd');
}

/** Trimmt Zeitstring auf HH:mm (z.B. "14:30:00" -> "14:30") */
export function toHHMM(time: string): string {
  return (time || '').slice(0, 5);
}

/** "1 Jahr" / "3 Jahren" — Dativ, wie in "vor 3 Jahren". */
const jahreDativ = (anzahl: number): string => `${anzahl} ${anzahl === 1 ? 'Jahr' : 'Jahren'}`;

/**
 * Abstand zu jetzt in Worten: unter einem Jahr genau, darüber gerundet.
 *
 * "vor 22 Tagen" und "vor 4 Tagen" sind die Angaben, auf die es im Büro
 * ankommt; bei drei Jahren genügt "vor fast 3 Jahren". Das genaue Datum steht
 * daneben im Tooltip.
 */
export function formatRelativePast(input: DateInput, jetzt: Date = new Date()): string {
  const d = toDate(input);
  if (!d) return String(input);
  if (d.getTime() > jetzt.getTime()) return formatDate(d);
  const { years = 0, months = 0, days = 0 } = intervalToDuration({ start: d, end: jetzt });

  if (years >= 1) {
    // Ab einem Jahr auf volle Jahre: "fast" rundet auf, "gut" zählt darüber.
    if (months >= 9) return `vor fast ${jahreDativ(years + 1)}`;
    if (months >= 1) return `vor gut ${jahreDativ(years)}`;
    return `vor ${jahreDativ(years)}`;
  }

  const teile = [
    months ? `${months} ${months === 1 ? 'Monat' : 'Monaten'}` : '',
    days ? `${days} ${days === 1 ? 'Tag' : 'Tagen'}` : '',
  ].filter(Boolean);
  if (teile.length === 0) return 'heute';
  return `vor ${teile.join(' und ')}`;
}

/** Alter als "10 Jahre, 3 Monate" — unterscheidet gleichnamige Profile auf einen Blick. */
export function formatAge(birthDate: DateInput, jetzt: Date = new Date()): string {
  const d = toDate(birthDate);
  if (!d || d.getTime() > jetzt.getTime()) return '';
  const { years = 0, months = 0 } = intervalToDuration({ start: d, end: jetzt });
  const monate = `${months} ${months === 1 ? 'Monat' : 'Monate'}`;
  if (years === 0) return monate;
  return `${years} ${years === 1 ? 'Jahr' : 'Jahre'}, ${monate}`;
}
